import { postGetAllWarehouses, postStorageHistory, postStorageLogAction } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { writeStorageStatus } from '../ManageStoragePage/ManageStoragePage.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const LOAD_CARD_REQUEST = 'app/StorageCardPage/LOAD_CARD_REQUEST';
export const LOAD_CARD_SUCCESS = 'app/StorageCardPage/LOAD_CARD_SUCCESS';
export const LOAD_CARD_ERROR = 'app/StorageCardPage/LOAD_CARD_ERROR';

export const LOAD_HISTORY_SUCCESS = 'app/StorageCardPage/LOAD_HISTORY_SUCCESS';
export const LOAD_HISTORY_ERROR = 'app/StorageCardPage/LOAD_HISTORY_ERROR';

export const LOAD_WAREHOUSES_SUCCESS = 'app/StorageCardPage/LOAD_WAREHOUSES_SUCCESS';

export const CHANGE_STATUS_REQUEST = 'app/StorageCardPage/CHANGE_STATUS_REQUEST';
export const CHANGE_STATUS_DONE = 'app/StorageCardPage/CHANGE_STATUS_DONE';
export const CHANGE_STATUS_ERROR = 'app/StorageCardPage/CHANGE_STATUS_ERROR';

export const ADD_PHOTO_REQUEST = 'app/StorageCardPage/ADD_PHOTO_REQUEST';
export const ADD_PHOTO_DONE = 'app/StorageCardPage/ADD_PHOTO_DONE';
export const ADD_PHOTO_ERROR = 'app/StorageCardPage/ADD_PHOTO_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  loading: false,
  loadError: null,
  history: [],
  historyError: null,
  warehouses: [],
  statusInProgress: false,
  statusError: null,
  photoInProgress: false,
  photoError: null,
};

const storageCardPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case LOAD_CARD_REQUEST:
      // Clear the previous card so a new id never paints the old set's facts.
      return { ...initialState, warehouses: state.warehouses, loading: true };
    case LOAD_CARD_SUCCESS:
      return { ...state, listing: payload, loading: false };
    case LOAD_CARD_ERROR:
      return { ...state, loadError: payload, loading: false };

    case LOAD_HISTORY_SUCCESS:
      return { ...state, history: payload, historyError: null };
    case LOAD_HISTORY_ERROR:
      return { ...state, historyError: payload };

    case LOAD_WAREHOUSES_SUCCESS:
      return { ...state, warehouses: payload };

    case CHANGE_STATUS_REQUEST:
      return { ...state, statusInProgress: true, statusError: null };
    case CHANGE_STATUS_DONE:
      return { ...state, statusInProgress: false };
    case CHANGE_STATUS_ERROR:
      return { ...state, statusInProgress: false, statusError: payload };

    case ADD_PHOTO_REQUEST:
      return { ...state, photoInProgress: true, photoError: null };
    case ADD_PHOTO_DONE:
      return { ...state, photoInProgress: false };
    case ADD_PHOTO_ERROR:
      return { ...state, photoInProgress: false, photoError: payload };

    default:
      return state;
  }
};

export default storageCardPageReducer;

// ================ Thunks ================ //

export const loadStorageHistory = listingId => async dispatch => {
  try {
    const response = await postStorageHistory({ listingId });
    dispatch({ type: LOAD_HISTORY_SUCCESS, payload: response?.data || [] });
  } catch (e) {
    console.error('Load storage history failed', e);
    dispatch({ type: LOAD_HISTORY_ERROR, payload: e });
  }
};

const loadWarehouses = () => async (dispatch, getState) => {
  try {
    const userId = getState().user.currentUser?.id?.uuid;
    const response = await postGetAllWarehouses({ UserId: userId });
    dispatch({ type: LOAD_WAREHOUSES_SUCCESS, payload: response?.data || [] });
  } catch (e) {
    // The card still works without the warehouse name — it shows the shelf.
    console.error('Load warehouses failed', e);
  }
};

/* P25009-485 BR-11 — the check-in photos: a square thumbnail for the card and
 * the scaled variants the lightbox picks from. */
const IMAGE_QUERY = {
  include: ['images'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.scaled-small',
    'variants.scaled-medium',
    'variants.scaled-large',
    'variants.scaled-xlarge',
  ],
};

const fetchListing = listingId => async (dispatch, getState, sdk) => {
  // `ownListings` returns drafts and privateData (the customer's contact
  // details) — a storage set is a draft forever, and only its owner may read it.
  const response = await sdk.ownListings.show({ id: new UUID(listingId), ...IMAGE_QUERY });
  return denormalisedResponseEntities(response)[0];
};

export const loadStorageCard = listingId => async dispatch => {
  dispatch({ type: LOAD_CARD_REQUEST });
  dispatch(loadWarehouses());
  dispatch(loadStorageHistory(listingId));
  try {
    const listing = await dispatch(fetchListing(listingId));
    dispatch({ type: LOAD_CARD_SUCCESS, payload: listing });
  } catch (e) {
    console.error('Load storage card failed', e);
    dispatch({ type: LOAD_CARD_ERROR, payload: e });
  }
};

/* BR-4 — record the print in the set's history. Fired when the print dialog
 * opens: the browser does not say whether the label actually came out. */
export const logLabelPrint = listingId => async dispatch => {
  try {
    await dispatch(logCardAction(listingId, 'storage-label-print'));
    dispatch(loadStorageHistory(listingId));
  } catch (e) {
    // A missing history row must never block the print itself.
    console.error('Log label print failed', e);
  }
};

const logCardAction = (listingId, action) => (dispatch, getState) => {
  const subUserId = getState().user.currentUser?.subUser?._id;
  return postStorageLogAction({ listingId, action, ...(subUserId ? { subUserId } : {}) });
};

/* BR-12 — the receipt opens in its own tab (a plain GET the server renders);
 * this only records it. Same caveat as the label: the browser does not say
 * whether anything was printed. */
export const logReceiptPrint = listingId => async dispatch => {
  try {
    await dispatch(logCardAction(listingId, 'storage-receipt-print'));
    dispatch(loadStorageHistory(listingId));
  } catch (e) {
    console.error('Log receipt print failed', e);
  }
};

/**
 * BR-11 — "Lägg till bild" on the card: upload the file, append it to the
 * set's images and log it in Historik.
 *
 * `images` on ownListings.update REPLACES the list, so the update names every
 * image the listing has now plus the new one — read fresh from the API rather
 * than from the page, so a photo added from another tab is not dropped.
 */
export const addCardPhoto = ({ listingId, file }) => async (dispatch, getState, sdk) => {
  dispatch({ type: ADD_PHOTO_REQUEST });
  try {
    const uploaded = await sdk.images.upload({ image: file }, { expand: true });
    const newImageId = uploaded.data.data.id;

    const current = await sdk.ownListings.show({ id: new UUID(listingId), include: ['images'] });
    const existingIds = (current.data.data.relationships?.images?.data || []).map(ref => ref.id);

    await sdk.ownListings.update({ id: new UUID(listingId), images: [...existingIds, newImageId] });

    const listing = await dispatch(fetchListing(listingId));
    dispatch({ type: LOAD_CARD_SUCCESS, payload: listing });
    dispatch({ type: ADD_PHOTO_DONE });

    try {
      await dispatch(logCardAction(listingId, 'storage-photo-add'));
    } catch (e) {
      // The photo is saved; a missing history row must not report it as failed.
      console.error('Log photo add failed', e);
    }
    dispatch(loadStorageHistory(listingId));
  } catch (e) {
    console.error('Add storage photo failed', e);
    dispatch({ type: ADD_PHOTO_ERROR, payload: e });
  }
};

export const changeCardStatus = ({ listingId, storageStatus, action }) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch({ type: CHANGE_STATUS_REQUEST });
  try {
    const currentUser = getState().user.currentUser;
    await writeStorageStatus(sdk, {
      listingId,
      providerUserId: currentUser?.id?.uuid,
      storageStatus,
      action,
      subUserId: currentUser?.subUser?._id,
    });
    const listing = await dispatch(fetchListing(listingId));
    dispatch({ type: LOAD_CARD_SUCCESS, payload: listing });
    dispatch({ type: CHANGE_STATUS_DONE });
    dispatch(loadStorageHistory(listingId));
  } catch (e) {
    console.error('Change storage status failed', e);
    dispatch({ type: CHANGE_STATUS_ERROR, payload: e });
  }
};
